import React, { Fragment } from "react";
import * as R from "ramda";
import * as Yup from "yup";
import { Modal, Row, Col } from "antd";
import { Formik, FieldArray, ErrorMessage } from "formik";
import { Input, Radio, Checkbox, InputNumber, Button } from "antd";

import { Field } from "../../../../../components/controls/FormikForm";
import { default as Error } from "../../../../../components/controls/FormikForm/ErrorMessage";

import { transformFromApiToForm, transformFromFormToApi } from "./selectors";

import styled from "styled-components/macro";

const ListLabel = styled.span`
  display: inline-block;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 12px;
`;

const StyledRadio = styled(Radio)`
  width: 100%;
  border-radius: 4px;
  padding: 6px 8px !important;
  border: 2px solid rgba(0, 0, 0, 0.1);

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  &.ant-radio-wrapper-checked {
    border: 2px solid ${({ theme }) => theme["@primary"]};
  }
`;

export default function QuestionModal({ question, t, saveQuestion, onCancel }) {
  const questionSchema = React.useCallback(
    Yup.object().shape({
      text: Yup.string()
        .ensure()
        .required(t("form:emptyFieldValidation")),
      type: Yup.string()
        .ensure()
        .required(t("form:emptyFieldValidation")),
      knockout: Yup.boolean(),
      options: Yup.array()
        .when("type", {
          is: type => type === "multiple",
          then: Yup.array().of(
            Yup.object().shape({
              text: Yup.string().required(t("form:emptyFieldValidation")),
              isValid: Yup.boolean()
            })
          )
        })
        .when(["type", "knockout"], {
          is: (type, knockout) => type === "multiple" && knockout,
          then: Yup.array().test(
            "has-valid-answer",
            t("opportunity:screening.allInvalidOptionsValidation"),
            options =>
              options &&
              options.reduce((acc, curr) => acc || curr.isValid, false)
          )
        }),
      min: Yup.number().when(["type", "knockout"], {
        is: (type, knockout) => type === "number" && knockout,
        then: Yup.number()
          .typeError(t("form:numberOnly"))
          .required(t("form:emptyFieldValidation"))
      }),
      max: Yup.number()

        .moreThan(Yup.ref("min"), t("opportunity:screening.moreThanMin"))
        .when(["type", "knockout"], {
          is: (type, knockout) => type === "number" && knockout,
          then: Yup.number()
            .typeError(t("form:numberOnly"))
            .required(t("form:emptyFieldValidation"))
        })
    }),
    [t]
  );

  return (
    <Formik
      initialValues={
        question
          ? transformFromApiToForm(question)
          : {
              text: "",
              type: "video"
            }
      }
      onSubmit={question => {
        saveQuestion(transformFromFormToApi(question));
        onCancel();
      }}
      validationSchema={questionSchema}
    >
      {({ handleSubmit, values, setValues }) => {
        return (
          <Modal
            visible
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={t("global:save")}
            cancelText={t("global:cancel")}
            title={
              question
                ? t("opportunity:screening.editQuestion")
                : t("opportunity:screening.addQuestion")
            }
          >
            <form
              data-test="question-form"
              onSubmit={e => {
                e.stopPropagation();
                handleSubmit(e);
              }}
            >
              <Field name="text" label={t("opportunity:screening.question")}>
                <Input.TextArea rows={2} />
              </Field>

              <Field
                name="type"
                label={t("opportunity:screening.responseType")}
                onChange={type =>
                  setValues({
                    text: values.text,
                    type: values.type,
                    ...(type === "multiple"
                      ? { options: [{ text: "" }, { text: "" }] }
                      : {}),
                    ...(type === "number"
                      ? { min: undefined, max: undefined }
                      : {})
                  })
                }
              >
                <Radio.Group defaultValue="video" buttonStyle="solid">
                  <StyledRadio value="video">
                    {t("opportunity:screening.video")}
                  </StyledRadio>
                  <StyledRadio value="number">
                    {t("opportunity:screening.number")}
                  </StyledRadio>
                  <StyledRadio value="multiple">
                    {t("opportunity:screening.multiple")}
                  </StyledRadio>
                </Radio.Group>
              </Field>

              {values.type === "number" && (
                <>
                  <Field
                    name="knockout"
                    valueProp="checked"
                    getValueFromEvent={R.path(["target", "checked"])}
                    onChange={knockout => {
                      if (!knockout) {
                        setValues(R.omit(["min", "max"], values));
                      }
                    }}
                  >
                    <Checkbox>
                      {t("opportunity:screening.knockoutNumber")}
                    </Checkbox>
                  </Field>
                  {values.knockout === true && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Field
                          name="min"
                          label={t("opportunity:screening.min")}
                        >
                          <InputNumber />
                        </Field>
                      </Col>
                      <Col span={12}>
                        <Field
                          name="max"
                          label={t("opportunity:screening.max")}
                        >
                          <InputNumber />
                        </Field>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {values.type === "multiple" && (
                <>
                  <Field
                    name="knockout"
                    valueProp="checked"
                    getValueFromEvent={R.path(["target", "checked"])}
                  >
                    <Checkbox>
                      {t("opportunity:screening.knockoutMultiple")}
                    </Checkbox>
                  </Field>
                  <FieldArray
                    name="options"
                    render={arrayHelpers => (
                      <>
                        <Row gutter={16}>
                          <Col span={19}>
                            <ListLabel>
                              {t("opportunity:screening.options")}
                            </ListLabel>
                          </Col>
                          {values.knockout && (
                            <Col span={3} style={{ textAlign: "center" }}>
                              <ListLabel>
                                {t("opportunity:screening.valid")}
                              </ListLabel>
                            </Col>
                          )}
                        </Row>
                        {values.options &&
                          values.options.map((option, index) => {
                            return (
                              <Fragment key={index}>
                                <Row gutter={16} type="flex" align="start">
                                  <Col span={values.knockout ? 19 : 22}>
                                    <Field name={`options.${index}.text`}>
                                      <Input />
                                    </Field>
                                  </Col>
                                  {values.knockout && (
                                    <Col
                                      span={3}
                                      style={{ textAlign: "center" }}
                                    >
                                      <Field
                                        name={`options.${index}.isValid`}
                                        valueProp="checked"
                                        getValueFromEvent={R.path([
                                          "target",
                                          "checked"
                                        ])}
                                        containerProps={{
                                          style: { marginTop: 5 }
                                        }}
                                      >
                                        <Checkbox disabled={!values.knockout} />
                                      </Field>
                                    </Col>
                                  )}
                                  <Button
                                    type="danger"
                                    icon="delete"
                                    onClick={() => arrayHelpers.remove(index)}
                                    disabled={values.options.length <= 2}
                                  />
                                </Row>
                              </Fragment>
                            );
                          })}
                        <ErrorMessage
                          name="options"
                          render={msg =>
                            typeof msg === "string" ? (
                              <Error style={{ marginBottom: 15 }}>{msg}</Error>
                            ) : null
                          }
                        />
                        <Button
                          icon="plus"
                          onClick={() =>
                            arrayHelpers.push({ text: "", isValid: false })
                          }
                          data-test="add-option-btn"
                        >
                          {t("opportunity:screening.addOption")}
                        </Button>
                      </>
                    )}
                  />
                </>
              )}
            </form>
          </Modal>
        );
      }}
    </Formik>
  );
}
